import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LogIn, Mail, Lock, AlertCircle } from 'lucide-react';
import axiosInstance, { API_BASE_URL } from '../config/axiosConfig';
import { notifyAuthStateChanged } from '../utils/authSession';

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [oauthStatus, setOauthStatus] = useState({ google: false, microsoft: false });
  
  // Task 25: Feature flags from environment
  const showTestHints = import.meta.env.VITE_SHOW_TEST_HINTS === 'true';
  const enableOAuth = import.meta.env.VITE_ENABLE_OAUTH === 'true';

  // Auto-redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem('spes_token');
    if (token) {
      navigate('/dashboard');
    }
    
    // Check for OAuth errors in URL params
    const oauthError = searchParams.get('error');
    const provider = searchParams.get('provider');
    const email = searchParams.get('email');
    
    if (oauthError) {
      handleOAuthError(oauthError, provider, email);
    }
    
    // Task 25: Only fetch OAuth status if feature is enabled
    if (enableOAuth) {
      fetchOAuthStatus();
    }
  }, [navigate, searchParams]);
  
  const fetchOAuthStatus = async () => {
    try {
      const response = await axiosInstance.get('/auth/oauth/status');
      if (response.data.oauth_enabled && response.data.providers) {
        setOauthStatus({
          google: response.data.providers.google?.enabled && response.data.providers.google?.configured,
          microsoft: response.data.providers.microsoft?.enabled && response.data.providers.microsoft?.configured
        });
      }
    } catch (error) {
      console.error('Failed to fetch OAuth status:', error);
    }
  };
  
  const handleOAuthError = (error, provider, email) => {
    const errorMessages = {
      'oauth_denied': `${provider} sign-in was cancelled.`,
      'missing_params': 'OAuth callback error. Please try again.',
      'invalid_state': 'Security validation failed. Please try again.',
      'verification_failed': 'Account verification failed. Please try again.',
      'email_not_verified': `Your ${provider} email is not verified. Please verify your email and try again.`,
      'no_user': email 
        ? `No Spes account found for ${email}. Please contact your administrator to link your account.`
        : 'No Spes account linked to this email. Please contact your administrator.',
      'oauth_failed': `${provider} sign-in failed. Please try again or use local login.`,
    };
    
    setErrorMessage(errorMessages[error] || 'An error occurred during sign-in.');
  };
  
  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE_URL}/auth/google/login`;
  };
  
  const handleMicrosoftLogin = () => {
    window.location.href = `${API_BASE_URL}/auth/ms/login`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    
    // Clear general error message
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // POST to /auth/login
      const response = await axiosInstance.post('/auth/login', {
        email: formData.email,
        password: formData.password,
      });

      if (response.status === 200 && response.data.access_token) {
        const { access_token, user } = response.data;

        // Save JWT token and user info to localStorage
        localStorage.setItem('spes_token', access_token);
        localStorage.setItem('spes_role', user.role);
        localStorage.setItem('spes_user', JSON.stringify(user));
        notifyAuthStateChanged();

        console.log('✅ Login successful:', user.name, '-', user.role);

        // Task 26: Check if password reset required
        if (user.must_change_password) {
          navigate('/reset-password');
          return;
        }

        // Task 26: Check if account is approved
        if (!user.approved) {
          setErrorMessage('Your account is pending approval. Please contact your administrator.');
          localStorage.clear();
          return;
        }

        // Redirect to dashboard
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);

      if (error.response) {
        // Server responded with error
        if (error.response.status === 401) {
          setErrorMessage('Unauthorized — Contact your administrator for access.');
        } else {
          setErrorMessage('An error occurred. Please try again later.');
        }
      } else if (error.request) {
        // No response from server
        setErrorMessage('Cannot connect to server. Please check if the backend is running.');
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-dark via-navy to-navy-light flex items-center justify-center px-4 sm:px-6 lg:px-8">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl"></div>
      </div>

      {/* Login Card */}
      <div className="relative max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-heading font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal via-teal-light to-gold">
            Spes Recruiter Portal
          </h1>
          <p className="mt-2 text-textLight/70 font-body">
            Sign in to manage your recruitment pipeline
          </p>
        </div>

        {/* Error Toast */}
        {errorMessage && (
          <div className="mb-6 bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start space-x-3 animate-shake">
            <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-red-400 text-sm font-body">{errorMessage}</p>
          </div>
        )}

        {/* Login Form Card */}
        <div className="bg-navy-dark/80 backdrop-blur-xl shadow-2xl rounded-2xl p-8 border border-teal/20">
          {/* Task 25: OAuth SSO Buttons (environment-driven) */}
          {enableOAuth && (oauthStatus.google || oauthStatus.microsoft) && (
            <div className="space-y-3 mb-6">
              <p className="text-sm text-center text-textLight/70 font-body mb-3">Sign in with</p>
              
              {oauthStatus.google && (
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center space-x-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg group"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Continue with Google</span>
                </button>
              )}
              
              {oauthStatus.microsoft && (
                <button
                  type="button"
                  onClick={handleMicrosoftLogin}
                  className="w-full flex items-center justify-center space-x-3 bg-[#2F2F2F] hover:bg-[#1F1F1F] text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg group"
                >
                  <svg className="w-5 h-5" viewBox="0 0 23 23">
                    <path fill="#f25022" d="M0 0h11v11H0z"/>
                    <path fill="#00a4ef" d="M12 0h11v11H12z"/>
                    <path fill="#7fba00" d="M0 12h11v11H0z"/>
                    <path fill="#ffb900" d="M12 12h11v11H12z"/>
                  </svg>
                  <span>Continue with Microsoft</span>
                </button>
              )}
              
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-teal/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-navy-dark text-textLight/50 font-body">Or continue with email</span>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-textLight/90 font-body mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="text-teal/70" size={20} />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-3 py-3 bg-navy/50 border ${
                    errors.email ? 'border-red-500' : 'border-teal/30'
                  } rounded-lg text-textLight placeholder-textLight/40 focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all font-body`}
                  placeholder="recruiter@spes.in"
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-400 font-body">{errors.email}</p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-textLight/90 font-body mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="text-teal/70" size={20} />
                </div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-3 py-3 bg-navy/50 border ${
                    errors.password ? 'border-red-500' : 'border-teal/30'
                  } rounded-lg text-textLight placeholder-textLight/40 focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all font-body`}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-400 font-body">{errors.password}</p>
              )}
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-teal to-teal-light hover:from-teal-light hover:to-teal text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-teal/50 disabled:opacity-50 disabled:cursor-not-allowed font-heading group"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <LogIn size={20} className="group-hover:translate-x-1 transition-transform" />
                  <span>Sign In</span>
                </>
              )}
            </button>

            {/* Task 28: Forgot Password Link */}
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-textLight/70 hover:text-teal transition-colors underline"
              >
                Forgot Password?
              </button>
            </div>
          </form>

          {/* Task 25: Test Credentials (only visible in development) */}
          {showTestHints && (
            <div className="mt-6 pt-6 border-t border-teal/20">
              <p className="text-xs font-semibold text-textLight/70 text-center font-body mb-2">
                Test Credentials (Development Only)
              </p>
              <div className="space-y-1 text-xs text-textLight/50 font-body">
                <p className="text-center"><strong className="text-gold">Superadmin:</strong> admin@spes.in / Admin@123</p>
                <p className="text-center"><strong className="text-teal">Manager:</strong> manager@spes.in / Manager@123</p>
                <p className="text-center"><strong className="text-blue-400">Recruiter:</strong> recruiter@spes.in / Recruiter@123</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <p className="mt-6 text-center text-xs text-textLight/40 font-body">
          {enableOAuth && (oauthStatus.google || oauthStatus.microsoft) 
            ? 'Secure authentication with SSO and JWT'
            : 'Secure authentication powered by JWT'
          }
        </p>
        
        {/* Task 25: Production branding */}
        <p className="mt-2 text-center text-xs text-textLight/30 font-body">
          {import.meta.env.VITE_APP_NAME || 'Spes Recruiter Portal'} {import.meta.env.VITE_APP_VERSION || ''}
        </p>
      </div>
    </div>
  );
}

export default Login;
